import React, { FC, Fragment, useEffect, useState, useContext, useCallback } from 'react';
import axios, { CancelTokenSource } from 'axios';
import {
  Grid,
  Container,
  Typography,
  makeStyles,
  Button,
  TextField,
  CircularProgress,
  Theme,
  FormHelperText,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Fade,
  RadioGroup,
  Radio,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { Page, StandardConfirmationDialog, PaperCustom, Breadcrumb, CardCustomer } from 'components';
import {
  PARTNER_BASE_URL,
  PURCHASE_INVOICE_BASE_URL,
  PURCHASE_PAYMENT_BASE_URL,
  GET_INVOICE_PAYMENT_DETAIL_BASE_URL,
  BANK_BASE_URL,
  RETURN_INVOICE_BASE_URL,
  GET_INVOICE_DETAIL_BASE_URL,
  PARTNER_DETAIL_BASE_URL,
  GET_INVOICE_PAYMENT_RETURN_BASE_URL,
  RETURN_BILL_BASE_URL,
  GET_PURCHASE_INVOICE_PAYMENT_DETAIL_BASE_URL,
  GET_DETAIL_BANK_BASE_URL
} from 'constants/url';
import { format } from 'date-fns';
import { dummyPartner, dummyBank, dummyPurchaseInvoicePayment, dummyInvoicePayment } from 'utils/dummy';
import { BACKGROUND, BLUE_PRIMARY, GRAY_3, GREEN, PRIMARY_MOBILE, RED_MOBILE, WHITE } from 'constants/colors';
import { diff } from 'utils';
import { CurrentUserContext } from 'contexts/CurrentUserContext';
import useRouter from 'hooks/useRouter';
import Autocomplete, { AutocompleteInputChangeReason } from '@material-ui/lab/Autocomplete';
import NumberFormat from 'components/NumberFormat';
import PaymentMethod from 'typings/enum/PaymentMethod';
import PaymentStatus from 'typings/enum/PaymentStatus';
import InvoiceModal from './components/InvoiceModal';
import InvoiceTable from './components/InvoiceTable';
import InvoiceReturnTable from './components/InvoiceReturnTable';
import InvoiceReturnModal from './components/InvoiceReturnModal';
import ConfirmPaymentModal from './components/ConfirmPaymentModal';
import NumberFormatMask from 'components/NumberFormatMask';
import _ from 'lodash';
import { useDropzone, FileWithPath } from 'react-dropzone';
import { AddRounded, CloseRounded, CloudUpload } from '@material-ui/icons';
import Close from '@material-ui/icons/Close';
import Authorization from '../PurchasePaymentPage/components/Authorization';
import GiroTable from './components/GiroTable';
import GiroModal from './components/GiroModal/GiroModal';
import StandardConfirmationDialogGiro from 'components/StandardConfirmationDialogGiro';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme: Theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff'
  },
  refresh: {
    backgroundColor: GREEN,
    color: WHITE,
    '&:hover': {
      backgroundColor: GREEN
    }
  },
  dropZone: {
    margin: '0 0.4em',
    padding: '1em',
    borderRadius: 2,
    border: `2px dashed ${BLUE_PRIMARY} `,
    backgroundColor: BACKGROUND,
    color: GRAY_3,
    outline: 'none',
    transition: 'border .24s ease-in-out',
    verticalAlign: 'middle',
    textAlign: 'center'
  },
  thumbsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  thumb: {
    position: 'relative',
    display: 'inline-flex',
    borderRadius: 2,
    border: '1px solid #eaeaea',
    marginRight: 20,
    width: 150,
    height: 150,
    boxSizing: 'border-box'
  },
  closeIcon: {
    position: 'absolute',
    top: -10,
    right: -10,
    fontSize: '2rem',
    backgroundColor: '#fff',
    borderRadius: '10em',
    cursor: 'pointer',
    boxShadow: '0 2px 5px rgb(0 0 0 / 14%)'
  },
  addIcon: {
    fontSize: '4rem',
    backgroundColor: '#fff',
    borderRadius: '10em',
    cursor: 'pointer',
    boxShadow: '0 2px 5px rgb(0 0 0 / 14%)'
  },
  thumbInner: {
    display: 'flex',
    minWidth: 0,
    overflow: 'hidden'
  },
  img: {
    display: 'block',
    width: 'auto',
    height: '100%'
  },
  iconUpload: {
    fontSize: 100,
    color: BLUE_PRIMARY
  }
}));

const PurchasePaymentUpdatePage: FC = () => {
  const classes = useStyles();
  const { history, location, match } = useRouter();
  const { currentUser } = useContext(CurrentUserContext);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.between('xs', 'sm'));
  const { id } = match.params;

  // eslint-disable-next-line
  // const params: any = location.state;

  const [snackbarVariant, setSnackbarVariant] = useState<'success' | 'error'>('success');
  const [message, setMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openSnackbarGiro, setOpenSnackbarGiro] = useState<boolean>(false);

  const [partner, setPartner] = useState<PartnerModel>(dummyPartner);
  const [partners, setPartners] = useState<PartnerModel[]>([dummyPartner]);

  const [queryString, setQueryString] = useState<string>('');
  const [returnNumber, setReturnNumber] = useState<string>('');
  const [partnerName, setPartnerName] = useState<string>('');
  const [count, setCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('id');

  const [paymentId, setPaymentId] = useState<number>(0);
  const [payDate, setPayDate] = useState<Date>(new Date());
  const [transferDate, setTransferDate] = useState<Date>(new Date());
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [giroBank, setGiroBank] = useState<string>('');
  const [giroNumber, setGiroNumber] = useState<string>('');
  const [giroAmount, setGiroAmount] = useState<number>(0);
  const [giroDueDate, setGiroDueDate] = useState<Date>(new Date());
  const [note, setNote] = useState<string>('');
  const [fullPayment, setFullPayment] = useState<boolean>(false);
  const [useReturn, setUseReturn] = useState<boolean>(false);
  const [totalPay, setTotalPay] = useState<number>(0);
  const [otherPiece, setOtherPiece] = useState<number>(0);
  const [currentOtherPiece, setCurrentOtherPiece] = useState<number>(0);
  const [currentTotalPay, setCurrentTotalPay] = useState<number>(0);
  const [currentTotalReturn, setCurrentTotalReturn] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [totalReturn, setTotalReturn] = useState<number>(0);

  const [image, setImage] = useState<{ path: string; file: File }[]>([]);
  const [imageBlob, setImageBlob] = useState<FileWithPath[]>([]);
  const [imageUrl, setImageUrl] = useState<string[]>([]);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isLoadingReturn, setLoadingReturn] = useState<boolean>(false);
  const [loadPartner, setLoadPartner] = useState<boolean>(false);
  const [loadingBank, setLoadingBank] = useState<boolean>(false);
  const [isUpdateLoading, setUpdateLoading] = useState<boolean>(false);

  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [isSubmit, setSubmit] = useState<boolean>(false);
  const [isFullReturn, setFullReturn] = useState<boolean>(false);

  const [totalPayError, setTotalPayError] = useState<string>('');
  const [paymentMessageError, setPaymentMessageError] = useState<string>('');
  const [paymentMethodMessage, setPaymentMethodMessage] = useState<string>('');
  const [paymentMethodImageMessage, setPaymentMethodImageMessage] = useState<string>('');
  const [payDateMessage, setPayDateMessage] = useState<string>('');

  const [giroBankMessage, setGiroBankMessage] = useState<string>('');
  const [giroNumberMessage, setGiroNumberMessage] = useState<string>('');
  const [giroAmountMessage, setGiroAmountMessage] = useState<string>('');
  const [giroDueDateMessage, setGiroDueDateMessage] = useState<string>('');
  const [giroMessage, setGiroMessage] = useState<string>('');

  const [openInvoice, setOpenInvoice] = useState<boolean>(false);
  const [invoices, setInvoices] = useState<(InvoiceModel & { type: 'return' | 'invoice' })[]>([]);
  const [invoiceReturn, setInvoiceReturn] = useState<InvoiceReturnModel[]>([]);

  const [checkedInvoice, setCheckedInvoice] = useState<(InvoiceModel & { type?: 'return' | 'invoice' })[]>([]);
  const [checkedReturn, setCheckedReturn] = useState<InvoicePaymentModel[]>([]);
  const [checkedInvoiceReturn, setCheckedInvoiceReturn] = useState<InvoiceReturnModel[]>([]);

  const [partnerMessage, setPartnerMessage] = useState<string>('');
  const [openReturn, setOpenReturn] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [bank, setBank] = useState<BankModel>(dummyBank);
  const [banks, setBanks] = useState<BankModel[]>([dummyBank]);

  const [giro, setGiro] = useState<GiroModalModel[]>([]);
  const [isCreateGiro, setIsCreateGiro] = useState<boolean>(false);
  const [editingGiro, setEditingGiro] = useState<GiroModalModel | null>(null);
  const [tempReturn, setTempReturn] = useState<number[]>([]);

  const [paymentMultiple, setPaymentMultiple] = useState<{ PurchaseInvoiceId: number; totalPay: number }[]>([]);

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    accept: ['image/jpeg', 'image/png'],
    maxFiles: 5,
    onDrop: (acceptedFiles: FileWithPath[]) => {
      // Append newly dropped files without resetting existing images
      uploadImage(acceptedFiles);
    }
  });

  const uploadImage = (files: FileWithPath[]) => {
    // Respect the 5-image limit including existing ones
    const remaining = Math.max(0, 5 - imageUrl.length);
    const nextFiles = files.slice(0, remaining);
    nextFiles.forEach((value: FileWithPath) => {
      setImageUrl(prev => [...prev, URL.createObjectURL(value)]);
      setImage(prev => [
        ...prev,
        {
          path: `${partnerName}${new Date().getTime()}-${Math.random()
            .toString(36)
            .slice(2, 8)}.${value.name.split('.').pop()}`,
          file: value as File
        }
      ]);
      setImageBlob(prev => [...prev, value]);
    });
  };

  const handleDeleteImage = (index: number): void => {
    setImageUrl(prev => prev.filter((_, key) => key !== index));
    setImage(prev => prev.filter((_, key) => key !== index));
    setImageBlob(prev => prev.filter((_, key) => key !== index));
  };

  const handleSearchBank = async (value: string) => {
    const params = new URLSearchParams();
    params.append('keyword', value);
    try {
      setLoadingBank(true);
      const { data } = await axios.get(`${BANK_BASE_URL}?${params.toString()}`);
      setBanks(data.data);
    } catch (error) {
      console.log('error', error);
      handleSnackBar(true, 'error', 'Potongan telah gagal');
    } finally {
      setLoadingBank(false);
    }
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const fetchData = async () => {
    setIsLoadingData(true);

    const getQueryParams = () => {
      const params = new URLSearchParams();
      if (queryString) {
        params.append('number', queryString);
      }

      if (orderBy || order) {
        params.append('orderBy', orderBy);
        params.append('ordered', order);
      }

      if (partner) {
        params.append('partnerId', String(partner.id));
      }
      params.append('status', PaymentStatus.UNPAID);
      params.append('page', currentPage.toString());
      params.append('isPiece', 'true');
      return params.toString();
    };

    try {
      const { data } = await axios.get(`${PURCHASE_INVOICE_BASE_URL}?${getQueryParams()}`, { cancelToken: cancelTokenSource.token });
      const { data: dataReturn } = await axios.get(`${RETURN_BILL_BASE_URL}?partnerId=${String(partner.id)}&status=UNPAID`, {
        cancelToken: cancelTokenSource.token
      });

      const newData = [
        ...data.data.map((value: any) => ({ ...value, type: 'invoice' })),
        ...dataReturn.data.map((value: any) => ({ ...value, type: 'return' }))
      ];
      setInvoices(newData);
      setCount(data.meta.last_page);
      setCurrentPage(data.meta.current_page);
    } catch (error) {
      console.log('error: ', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const fetchReturnInvoice = async () => {
    setLoadingReturn(true);

    const getQueryParams = () => {
      const params = new URLSearchParams();

      if (returnNumber) {
        params.append('number', returnNumber);
      }

      params.append('partnerId', partner.id.toString());
      params.append('usageStatus', 'false');
      params.append('perPage', '1000000');
      return params.toString();
    };

    try {
      const { data } = await axios.get(`${RETURN_INVOICE_BASE_URL}?${getQueryParams()}`, { cancelToken: cancelTokenSource.token });
      setInvoiceReturn(data.data);
    } catch (error) {
      console.log('error: ', error);
    } finally {
      setLoadingReturn(false);
    }
  };

  const fecthInvoicePayment = async () => {
    setIsLoadingData(true);
    try {
      const { data } = await axios.get(GET_PURCHASE_INVOICE_PAYMENT_DETAIL_BASE_URL(+id));

      const returnIds = (data.data.ReturnId ?? []).map((v: any) => v.ReturnId);
      setPaymentMethod(data.data.paymentMethod);
      setPayDate(new Date(data.data.payDate));
      setNote(data.data.note);
      setUpdate(true);
      setTotalPay(data.data.totalPay);
      setCurrentTotalPay(data.data.totalPay);
      setFullReturn(data.data.paymentMethod === PaymentMethod.RETURN);
      setUseReturn(data.data.useReturn);
      setTempReturn(returnIds);
      setGiroBank(data.data.giroBank);
      setTransferDate(new Date(data.data.transferDate));
      setGiroDueDate(new Date(data.data.giroDueDate));
      setGiroAmount((data.data.giro ?? []).reduce((acc: any, curr: any) => acc + curr.giroAmount, 0));
      setGiroNumber(data.data.giroNumber);
      setGiro(data.data.giro);
      setOtherPiece(data.data.otherPiece);
      setCurrentOtherPiece(data.data.otherPiece);
      // Load existing payment images into state for display and later sync
      const existingImages = (data.data.PurchaseInvoicePaymentImages ?? []).map((img: any) => ({ path: img.path, file: undefined as any }));
      const existingImageUrls = (data.data.PurchaseInvoicePaymentImages ?? []).map((img: any) => img.url);
      setImage(existingImages);
      setImageUrl(existingImageUrls);
      fetchAll(
        data.data.PartnerId,
        data.data.PurchaseInvoicePaymentMultiple.map((v: any) => v.PurchaseInvoiceId),
        returnIds,
        data.data.BankId
      );
      setPaymentMultiple(
        data.data.PurchaseInvoicePaymentMultiple.map((v: any) => ({ PurchaseInvoiceId: v.PurchaseInvoiceId, totalPay: v.totalPay }))
      );
    } catch (error) {
      console.log('error: ', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const fetchAll = (prtId: number, invId: number[], rtnId: number[], bankId: number) => {
    setUpdateLoading(true);
    Promise.all([
      axios.get(PARTNER_DETAIL_BASE_URL(prtId)).catch(() => undefined),
      axios.get(`${PURCHASE_INVOICE_BASE_URL}?ids=${invId.join(',')}&full=true`).catch(() => undefined),
      axios.get(`${RETURN_INVOICE_BASE_URL}?id=${rtnId.join(',')}`).catch(() => undefined),
      axios.get(`${PURCHASE_PAYMENT_BASE_URL}/children?paymentId=${id}`).catch(() => undefined),
      axios.get(GET_DETAIL_BANK_BASE_URL(bankId)).catch(() => undefined)
    ])
      .then((result: any) => {
        const [dataPartner, dataPurchaseInvoice, dataReturn, dataChildren, dataBank] = result;

        if (dataPartner) {
          setPartner(dataPartner.data.data);
          setPartners([dataPartner.data.data]);
        }

        if (dataPurchaseInvoice) {
          setCheckedInvoice(dataPurchaseInvoice.data.data);
        }

        if (dataReturn) {
          setCheckedInvoiceReturn(dataReturn.data.data);
        }

        if (dataChildren) {
          setCheckedReturn(dataChildren.data.data);
          setCurrentTotalReturn(dataChildren.data.data.reduce((acc: any, curr: any) => acc + curr.totalPay, 0));
        }

        if (dataBank) {
          setBank(dataBank.data.data);
          setBanks([dataBank.data.data]);
        }
      })
      .finally(() => {
        setUpdateLoading(false);
      });
  };

  const fetchDeleteReturn = async (id: number) => {
    try {
      await axios.delete(`${PURCHASE_PAYMENT_BASE_URL}/return/${id}`);
    } catch (e) {
      console.log('error', e);
    } finally {
      setRefresh(true);
      setPaymentMessageError('MOHON UNTUK MELAKUKAN MUAT ULANG.');
    }
  };

  const handleSearchPartner = async () => {
    const params = new URLSearchParams();
    params.append('keyword', partnerName);
    params.append('partnerType', 'SUPPLIER');

    setLoadPartner(true);
    setPartnerMessage('');

    try {
      const { data } = await axios.get(`${PARTNER_BASE_URL}?${params.toString()}`);
      setPartners(data.data);
    } catch (error) {
      console.log('error :', error);
    } finally {
      setLoadPartner(false);
    }
  };

  const handleOnRefresh = () => {
    setCheckedReturn([]);
    setCheckedInvoiceReturn([]);
    setCheckedInvoice([]);
    setGiro([]);
    setUseReturn(false);
    setPaymentMethod('');
    setNote('');
    setPartner(dummyPartner);
    setTotalPay(0);
    setTotalPrice(0);
    setTotalReturn(0);
    setFullReturn(false);
    setCurrentTotalPay(0);
    setCurrentTotalReturn(0);
    setRefresh(false);
    setPaymentMessageError('');

    fecthInvoicePayment();
  };

  const validation = () => {
    let valid = true;

    if (!isFullReturn) {
      if (!totalPay) {
        setTotalPayError('Jumlah Bayar tidak boleh 0.');
        valid = false;
      }

      // if (totalPay < 0) {
      //   setTotalPayError('Jumlah Bayar tidak boleh kurang dari 0.');
      //   valid = false;
      // }

      if (totalPay > totalPrice - totalReturn + otherPiece && otherPiece > 0) {
        setTotalPayError('Jumlah Bayar tidak boleh lebih dari Total Pembayaran.');
        valid = false;
      }

      if (totalPay > totalPrice - totalReturn && otherPiece > 0) {
        setTotalPayError('Jumlah Bayar tidak boleh lebih dari Total Pembayaran.');
        valid = false;
      }
    }

    // if (paymentMethod === PaymentMethod.BANK_TRANSFER && image.length < 1) {
    //   setPaymentMethodImageMessage('lampiran tidak boleh kosong.');
    //   valid = false;
    // }

    if (paymentMethod === '' || !paymentMethod) {
      setPaymentMethodMessage('Metode Pembayaran tidak boleh kosong.');
      valid = false;
    }

    if (!payDate) {
      setPayDateMessage('Tanggal Pembayaran tidak boleh kosong.');
      valid = false;
    }

    if (!partner || partner.id === 0) {
      setPartnerMessage('Supplier tidak boleh kosong');
      valid = false;
    }

    if (checkedInvoice.length === 0) {
      setPaymentMessageError('Invoice tidak boleh kosong');
      valid = false;
    }

    if (useReturn && checkedReturn.length === 0) {
      setPaymentMessageError('Jika menggunakan return mohon pilih invoice return terlebih dahulu');
      valid = false;
    }

    if (paymentMethod === PaymentMethod.GIRO) {
      // if (!giroBank) {
      //   setGiroBankMessage('Bank Penerbit Giro tidak boleh kosong.');
      //   valid = false;
      // }

      // if (!giroNumber) {
      //   setGiroNumberMessage('Nomor Giro tidak boleh kosong.');
      //   valid = false;
      // }

      // if (!giroAmount) {
      //   setGiroAmountMessage('Nominal Giro tidak boleh kosong.');
      //   valid = false;
      // }

      if (!giro.length) {
        setGiroMessage('Data Giro belum dipilih');
        valid = false;
      }

      if (giroAmount === 0) {
        setGiroAmountMessage('Nominal Giro tidak boleh 0.');
        valid = false;
      } else if (giroAmount != totalPay && otherPiece > 0) {
        setGiroAmountMessage('Nominal Giro harus sama dengan Jumlah Pembayaran.');
        valid = false;
      }

      if (!giroDueDate) {
        setGiroDueDateMessage('Tanggal Jatuh Tempo Giro tidak boleh kosong.');
        valid = false;
      }
    }

    // if (isFullReturn || paymentMethod === PaymentMethod.RETURN) {
    //   if (totalPrice !== totalReturn) {
    //     setPaymentMessageError('Jika Pembayaran penuh menggunakan RETURN total pembayaran harus sesuai dengan jumlah Return');
    //     valid = false;
    //   }
    // }

    return valid;
  };

  const resetValidation = () => {
    setTotalPayError('');
    setPaymentMethodMessage('');
    setPaymentMethodImageMessage('');
    setPayDateMessage('');
    setPartnerMessage('');
    setPaymentMessageError('');
    setGiroAmountMessage('');
    setGiroMessage('');
  };

  const handleSnackBar = (open: boolean, variant: 'success' | 'error', message: string): void => {
    setSnackbarVariant(variant);
    setOpenSnackbar(open);
    setMessage(message);
  };

  const handleCloseSnackbar = (): void => {
    setOpenSnackbar(false);
    handleCloseConfirm();
  };

  const handleCloseSnackbarGiro = (): void => {
    setOpenSnackbarGiro(false);
  };

  const handleConfirmSnackbar = (): void => {
    setOpenSnackbar(false);
    history.push(`/pembayaran-pembelian/${paymentId}`);
  };
  const handleConfirmSnackbarGiro = (): void => {
    setOpenSnackbar(false);
  };

  const handleCancel = () => {
    history.push('/pembayaran-pembelian');
  };

  const individualCheck = (item: { InvoiceId: number }) => {
    let currentChecked = [...checkedInvoice];
    if (currentChecked.some(value => value.id === item.InvoiceId)) {
      currentChecked = currentChecked.filter(value => value.id !== item.InvoiceId);
    } else {
      const findInvoice = invoices.find(value => value.id === item.InvoiceId);
      if (findInvoice) {
        currentChecked = [...currentChecked, findInvoice];
      }
    }

    setCheckedInvoice(currentChecked);
    setTotalPay(p => p - paymentMultiple.reduce((acc, curr) => acc + (curr.PurchaseInvoiceId === item.InvoiceId ? curr.totalPay : 0), 0));
    if (isFullReturn) {
      resetForm();
    }
  };

  const individualCheckGiro = (selectedGiro: GiroModalModel) => {
    let currentGiro = [...giro];
    const newGiro = currentGiro.filter(g => g.giroNumber !== selectedGiro.giroNumber);
    const totalNominal = newGiro.reduce((sum, item) => sum + item.giroAmount, 0);

    setGiro(newGiro);
    setGiroAmount(totalNominal);
    setTotalPay(totalNominal);
  };

  const handleReturnInvoiceCheck = (item: InvoiceReturnModel) => {
    handleNotFullPayment();
    let newCheckedInvoiceReturn = [...checkedInvoiceReturn];
    const exists = newCheckedInvoiceReturn.some(value => value.id === item.id);
    if (exists) {
      newCheckedInvoiceReturn = newCheckedInvoiceReturn.filter(value => value.id !== item.id);
      setCheckedReturn(p => p.filter(f => f.ReturnId !== item.id));
    } else {
      newCheckedInvoiceReturn = [...newCheckedInvoiceReturn, item];
      const _totalPrice = totalPrice - totalPay - totalReturn;
      setCheckedReturn(p => [
        ...p,
        {
          ...dummyInvoicePayment,
          ReturnId: item.id,
          useReturn: true,
          paymentMethod: PaymentMethod.RETURN,
          payDate: new Date(),
          autorisasi: false,
          totalPay:
            item.totalPrice - (item.totalUsage || 0) <= _totalPrice
              ? item.totalPrice - (item.totalUsage || 0)
              : item.totalPrice - (item.totalUsage || 0) - diff(_totalPrice, item.totalPrice - (item.totalUsage || 0))
        }
      ]);
    }
    setCheckedInvoiceReturn(newCheckedInvoiceReturn);
  };

  const handleUseReturn = () => {
    setCheckedInvoiceReturn([]);
    setCheckedReturn([]);

    setUseReturn(prevState => {
      if (prevState && isFullReturn) {
        setPaymentMethod('');
        resetForm();
      }
      return !prevState;
    });

    setFullReturn(false);
    handleNotFullPayment();
  };

  const handleClickOpenInvoiceModal = () => {
    setOpenInvoice(true);
  };

  const handleClickOpenGiroModal = () => {
    setEditingGiro(null);
    setIsCreateGiro(true);
  };
  const handleClickCLoseGiroModal = () => {
    setEditingGiro(null);
    setIsCreateGiro(false);
  };

  const handleEditGiro = (selectedGiro: GiroModalModel) => {
    setEditingGiro(selectedGiro);
    setIsCreateGiro(true);
  };

  const handleCloseInvoiceModal = () => {
    setOpenInvoice(false);
  };

  const handleCancelInvoice = () => {
    setCheckedInvoice([]);
    setOpenInvoice(false);
  };

  const handleOnSubmit = () => {
    resetValidation();

    if (!validation()) {
      return;
    }

    handleOpenConfirm();
  };

  const handleDoSubmit = async () => {
    setSubmit(true);

    try {
      const parseReturn = checkedReturn.map(value => ({
        id: value.id,
        paymentNumber: value.paymentNumber,
        PaymentId: value.PaymentId,
        ReturnId: value.ReturnId,
        useReturn: value.useReturn,
        PurchaseInvoiceId: value.InvoiceId,
        paymentMethod: value.paymentMethod,
        payDate: value.payDate,
        note: value.note,
        BankId: value.BankId && value.BankId > 0 ? value.BankId : null,
        totalPay: value.totalPay
      }));

      const { data } = await axios.post(`${PURCHASE_PAYMENT_BASE_URL}/update`, {
        id: +id,
        PurchaseInvoiceId: checkedInvoice.map(val => ({ id: val.id, type: val.type })),
        BankId: bank.id || null,
        paymentMethod,
        note,
        totalPay,
        useReturn: checkedReturn.length > 0,
        returns: parseReturn,
        payDate,
        otherPiece,
        giroBank,
        transferDate: paymentMethod === PaymentMethod.BANK_TRANSFER ? transferDate : null,
        giroDueDate,
        image: image || [],
        giroAmount,
        giroNumber,
        status: paymentMethod === PaymentMethod.GIRO ? PaymentStatus.UNPAID : PaymentStatus.PAID,
        giro: giro,
        partnerId: partner.id
      });
      setPaymentId(+id);
      if (data.data.PurchaseInvoicePaymentImages && data.data.PurchaseInvoicePaymentImages.length > 0) {
        data.data.PurchaseInvoicePaymentImages.map(async (value: any) => {
          const frontendImage = image.find(img => img.path === value.path);
          // Only upload newly added files (existing images have no File attached)
          if (frontendImage && frontendImage.file) {
            const file = frontendImage.file;
            const url = value.url;
            const myHeaders = new Headers();
            const inferredType = file.type || (file.name.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg');
            myHeaders.append('Content-Type', inferredType);

            fetch(url, {
              method: 'PUT',
              body: file,
              headers: myHeaders
            })
              .then(response => response.text())
              .then(result => console.log(result))
              .catch(error => console.log('error', error));
          }
        });
      }

      handleSnackBar(true, 'success', 'Pembayaran berhasil dibuat');
    } catch (error) {
      console.log('error :', error);
      handleSnackBar(true, 'error', '500 Error internal server');
    } finally {
      setSubmit(false);
    }
  };

  const handleFullPayment = () => {
    setFullPayment(true);
    setTotalPay(totalPrice - totalReturn - otherPiece);
    setTotalPayError('');
  };

  const handleNotFullPayment = () => {
    setFullPayment(false);
    setTotalPayError('');
  };

  const handleOpenReturn = () => {
    setOpenReturn(true);
  };

  const handleCloseReturn = () => {
    setOpenReturn(false);
  };

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const resetForm = () => {
    setNote('');
    setPaymentMethod('');
    setBank(dummyBank);
    setGiroBank('');
    setGiroNumber('');
    setGiroAmount(0);
    setGiroDueDate(new Date());
  };

  const resetValue = () => {
    setGiro([]);
    setEditingGiro(null);
    setInvoices([]);
    setCheckedReturn([]);
    setCheckedInvoiceReturn([]);
    setCheckedInvoice([]);
    setTotalPay(0);
    setUseReturn(false);
    setFullReturn(false);
  };

  const handleAddGiro = (newGiro: GiroModalModel) => {
    setGiro(prev => {
      const nextGiro = editingGiro
        ? prev.map(item => (item.giroNumber === editingGiro.giroNumber ? newGiro : item))
        : [...prev, newGiro];
      const totalNominal = nextGiro.reduce((sum, item) => sum + item.giroAmount, 0);
      setGiroAmount(totalNominal);
      setTotalPay(totalNominal);
      return nextGiro;
    });
    setOpenSnackbarGiro(false);
    setGiroMessage('');
    setEditingGiro(null);
  };

  const handleChange = (value: string) => {
    if (value === '') {
      setOtherPiece(0);
      setTotalPayError('');
      return;
    }

    const parsedValue = parseFloat(value);
    if (isNaN(parsedValue)) {
      setTotalPayError('Masukkan angka valid');
      return;
    }
    setOtherPiece(parsedValue);
    setTotalPayError('');
  };

  const handleChangeTotalPay = (value: number) => {
    setTotalPay(value);
  };

  const formatNumberManual = (num: any) => {
    if (num === null || num === '') return '';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  useEffect(() => {
    if (checkedInvoice.length === 0) {
      setTotalPrice(0);
      return;
    }

    const _totalPrice = checkedInvoice
      .map(value => value.totalPrice - value.totalPay + (paymentMultiple.find(f => f.PurchaseInvoiceId === value.id)?.totalPay ?? 0))
      .reduce((a, b) => a + b, 0);
    setTotalPrice(_totalPrice);
  }, [checkedInvoice, isUpdate, currentTotalPay, currentTotalReturn, paymentMultiple]);

  useEffect(() => {
    if (checkedReturn.length === 0) {
      setTotalReturn(0);
      return;
    }

    const _totalReturn = checkedReturn.map(value => value.totalPay).reduce((a, b) => a + b, 0);

    const finalReturn = _totalReturn + currentTotalReturn;
    const _isFullReturn = finalReturn === totalPrice;

    if (!isFullReturn && finalReturn === totalPrice) {
      resetForm();
    }

    setTotalReturn(_totalReturn);
    setFullReturn(_isFullReturn);
    setPaymentMethod(_isFullReturn ? PaymentMethod.RETURN : paymentMethod);
    setTotalPay(_isFullReturn ? finalReturn : totalPay);
  }, [checkedReturn, totalPrice, isUpdate, totalPay]);

  useEffect(() => {
    if (!id) return;
    fecthInvoicePayment();
  }, [id]);

  useEffect(() => {
    if (!partner) return;

    if (partner.id < 1) {
      if (isFullReturn) {
        resetForm();
        resetValue();
      } else {
        resetValue();
      }
    }
  }, [partner]);

  useEffect(() => {
    if (!openInvoice) return;
    const debouncedFetchHits = _.debounce(fetchData, 500);
    debouncedFetchHits();
    return () => cancelTokenSource.cancel('No longer latest query');
  }, [queryString, orderBy, order, currentPage, openInvoice]);

  useEffect(() => {
    if (!openReturn) return;

    const debouncedFetchHits = _.debounce(fetchReturnInvoice, 500);
    debouncedFetchHits();
    return () => cancelTokenSource.cancel('No longer latest query');
  }, [openReturn, returnNumber]);

  useEffect(() => {
    const debouncedFetchHits = _.debounce(handleSearchPartner, 500);
    debouncedFetchHits();
    return () => cancelTokenSource.cancel('No longer latest query');
  }, [partnerName]);

  useEffect(() => {
    if (fullPayment) {
      handleFullPayment();
    }
  }, [otherPiece]);

  useEffect(() => {
    setFullPayment(totalPay + totalReturn + otherPiece === totalPrice);
  }, [totalPay, totalPrice]);

  console.log(totalPay + totalReturn + otherPiece);

  return (
    <Page title='Buat Pembayaran Pembelian'>
      <Container>
        <Grid container direction='row'>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Typography variant='h1'> Buat Pembayaran Pembelian</Typography>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Breadcrumb />
          </Grid>
        </Grid>

        <PaperCustom>
          <Grid container direction='row' spacing={2}>
            <Grid container direction='row' spacing={1} item xl={12} lg={12} md={12} sm={12} xs={12}>
              <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                <Autocomplete
                  id='partner'
                  value={partner}
                  options={partners}
                  getOptionLabel={option => option.name}
                  getOptionSelected={(option, value) => {
                    return value.id === option.id;
                  }}
                  onChange={(event: any, value: any) => {
                    if (value) {
                      setPartner(value);
                      resetValue();
                    }
                  }}
                  onOpen={e => setPartnerName('')}
                  onInputChange={(event: React.ChangeEvent<{}>, value: string, reason: AutocompleteInputChangeReason) => {
                    if (reason === 'clear') {
                      setPartner(dummyPartner);
                      setPartners([dummyPartner]);
                    }
                  }}
                  loading={loadPartner}
                  renderOption={(option, state) => (
                    <CardCustomer partner={option} isLoading={loadPartner} isSelected={option.id === partner.id && !loadPartner} />
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      required
                      label='Pilih Supplier'
                      onChange={e => {
                        setPartnerName(e.target.value);
                      }}
                      error={partnerMessage !== ''}
                      helperText={partnerMessage}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <Fragment>
                            {loadPartner && <CircularProgress color='inherit' size={20} />}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        )
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xl={paymentMethod === PaymentMethod.BANK_TRANSFER ? 3 : 6}
                lg={paymentMethod === PaymentMethod.BANK_TRANSFER ? 3 : 6}
                md={paymentMethod === PaymentMethod.BANK_TRANSFER ? 3 : 6}
                xs={12}
                sm={12}
              >
                <TextField
                  id='paymentMethod'
                  required
                  label='Metode Pembayaran'
                  disabled={isFullReturn}
                  value={paymentMethod as string}
                  error={paymentMethodMessage !== ''}
                  helperText={paymentMethodMessage}
                  onChange={e => {
                    resetForm();
                    setPaymentMethod(e.target.value as string);
                    setFullReturn(e.target.value === PaymentMethod.RETURN);
                  }}
                  select
                  fullWidth
                >
                  <MenuItem key={'1'}>Metode Pembayaran</MenuItem>
                  {isFullReturn ? (
                    <MenuItem key={'2'} value={PaymentMethod.RETURN}>
                      {PaymentMethod.RETURN}
                    </MenuItem>
                  ) : (
                    Object.entries(PaymentMethod).map(([key, value]) => (
                      <MenuItem key={key} value={value}>
                        {value}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>

              {paymentMethod === PaymentMethod.BANK_TRANSFER && (
                <Grid item xl={3} lg={3} md={3} xs={12} sm={12}>
                  <Autocomplete
                    id='bank'
                    value={bank}
                    options={banks}
                    getOptionLabel={option => option.bankName}
                    getOptionSelected={(option, value) => {
                      return value.id === option.id;
                    }}
                    onChange={({}, value: any) => {
                      if (value) {
                        setBank(value);
                        setNote(`Transfer Rekening: (${value.bankCode}) ${value.bankName} - No Rek : ${value.bankNumber} / ${value.bankAccount}`);
                      }
                    }}
                    onOpen={e => handleSearchBank('')}
                    loading={loadingBank}
                    renderInput={params => (
                      <TextField
                        {...params}
                        required
                        label='Pilih Bank'
                        onChange={e => handleSearchBank(e.target.value)}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <Fragment>
                              {loadingBank && <CircularProgress color='inherit' size={20} />}
                              {params.InputProps.endAdornment}
                            </Fragment>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* {paymentMethod === PaymentMethod.GIRO && (
                <Grid item xl={3} lg={3} md={3} xs={12} sm={12}>
                  <TextField
                    id='bank'
                    required
                    fullWidth
                    type='text'
                    label='Bank Penerbit'
                    placeholder=''
                    value={giroBank}
                    onChange={e => setGiroBank(e.target.value)}
                    error={giroBankMessage !== ''}
                    helperText={giroBankMessage}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
              )} */}

              <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                {paymentMethod === PaymentMethod.BANK_TRANSFER && (
                  <TextField
                    id='payDate'
                    required
                    fullWidth
                    type='date'
                    label='Tanggal Transfer'
                    placeholder=''
                    value={format(transferDate, 'yyyy-MM-dd')}
                    onChange={e => setTransferDate(new Date(e.target.value))}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                )}
                <TextField
                  id='payDate'
                  required
                  fullWidth
                  type='date'
                  label='Tanggal Kwitansi'
                  placeholder=''
                  value={format(payDate, 'yyyy-MM-dd')}
                  onChange={e => setPayDate(new Date(e.target.value))}
                  error={payDateMessage !== ''}
                  helperText={payDateMessage}
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>

              {/* {paymentMethod === PaymentMethod.GIRO && (
                <Grid item xl={3} lg={3} md={3} xs={12} sm={12}>
                  <TextField
                    id='bank'
                    required
                    fullWidth
                    type='text'
                    label='Nomor Giro'
                    placeholder=''
                    value={giroNumber}
                    onChange={e => setGiroNumber(e.target.value)}
                    error={giroNumberMessage !== ''}
                    helperText={giroNumberMessage}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
              )} */}

              {paymentMethod === PaymentMethod.GIRO && (
                <Grid item xl={6} lg={6} md={6} xs={12} sm={12}>
                  <TextField
                    id='bank'
                    required
                    fullWidth
                    type='text'
                    label='Nominal Giro'
                    placeholder=''
                    value={giroAmount}
                    onChange={e => setGiroAmount(+e.target.value)}
                    error={giroAmountMessage !== ''}
                    helperText={giroAmountMessage}
                    InputProps={{ inputComponent: NumberFormatMask as any }}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
              )}

              {paymentMethod === PaymentMethod.GIRO && (
                <Grid container spacing={1}>
                  <Grid item xl={6} lg={6} md={6} xs={12} sm={12}>
                    &nbsp;
                  </Grid>
                  <Grid item xl={6} lg={6} md={6} xs={12} sm={12}>
                    <TextField
                      id='giroDueDate'
                      required
                      fullWidth
                      type='date'
                      label='Tanggal Jatuh Tempo Giro'
                      placeholder=''
                      value={format(giroDueDate, 'yyyy-MM-dd')}
                      onChange={e => setGiroDueDate(new Date(e.target.value))}
                      error={giroDueDateMessage !== ''}
                      helperText={giroDueDateMessage}
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              {paymentMethod === PaymentMethod.GIRO && (
                <Grid item xl={6} lg={6} md={6} xs={12} sm={12}>
                  &nbsp;
                </Grid>
              )}
              <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                <TextField fullWidth id='note' label='Catatan' multiline rows={4} value={note} onChange={e => setNote(e.target.value)} />
              </Grid>
              {paymentMethod === PaymentMethod.BANK_TRANSFER && (
                <Grid xs={12} {...getRootProps({ className: classes.dropZone })}>
                  {imageUrl.length < 5 && <input id='payment-images-input' {...getInputProps({ id: 'payment-images-input' })} />}
                  {imageUrl.length > 0 ? (
                    <aside className={classes.thumbsContainer}>
                      {imageUrl.map((value, index) => (
                        <div className={classes.thumb} key={index}>
                          <div className={classes.thumbInner}>
                            <img src={value} key={index} className={classes.img} />
                          </div>
                          <CloseRounded className={classes.closeIcon} color='error' onClick={() => handleDeleteImage(index)} />
                        </div>
                      ))}
                      {imageUrl.length > 0 && imageUrl.length < 5 && (
                        <label htmlFor='payment-images-input'>
                          <AddRounded className={classes.addIcon} color='primary' />
                        </label>
                      )}
                    </aside>
                  ) : (
                    <>
                      <CloudUpload className={classes.iconUpload} />
                      <Typography align='center'> Geser dan lepaskan disini untuk mengupload gambar PNG, JPEG, JPG, Max 5 *</Typography>
                    </>
                  )}
                </Grid>
              )}
              {paymentMethodImageMessage !== '' && <Typography color='error'>{paymentMethodImageMessage}</Typography>}
            </Grid>
            {paymentMethod === PaymentMethod.GIRO && (
              <>
                <Grid container direction='row' item xl={12} md={12} sm={12}>
                  <GiroTable giro={giro} handleIndividualCheck={individualCheckGiro} handleEditGiro={handleEditGiro} />
                </Grid>

                {giroMessage ? (
                  <Grid container direction='row' item xl={12} md={12} sm={12}>
                    <FormHelperText error>Data Giro tidak boleh kosong.</FormHelperText>
                  </Grid>
                ) : null}

                <Grid container direction='row' item xl={12} md={12} sm={12} spacing={1}>
                  <Grid item>
                    <Button disabled={partner.id === 0} variant='outlined' onClick={handleClickOpenGiroModal}>
                      Tambah Giro
                    </Button>
                  </Grid>

                  <Grid item>
                    {isUpdate && (
                      <Button onClick={handleOnRefresh} className={classes.refresh}>
                        Memuat Ulang
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </>
            )}
            <Grid container direction='row' item xl={12} md={12} sm={12}>
              <FormHelperText error={paymentMessageError !== ''}>{paymentMessageError}</FormHelperText>
              <InvoiceTable
                isLoadingData={isLoadingData || isUpdateLoading}
                isUpdate={isUpdate}
                checkedInvoice={checkedInvoice}
                paymentMultiple={paymentMultiple}
                handleIndividualCheck={individualCheck}
              />
            </Grid>
            <Grid container direction='row' item xl={12} md={12} sm={12} spacing={1}>
              <Grid item>
                <Button disabled={partner.id === 0} variant='outlined' onClick={handleClickOpenInvoiceModal}>
                  Pilih Invoice Rujukan
                </Button>
              </Grid>

              <Grid item>
                {isUpdate && (
                  <Button onClick={handleOnRefresh} className={classes.refresh}>
                    Memuat Ulang
                  </Button>
                )}
              </Grid>
            </Grid>
            <Grid item lg={12} sm={12} md={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    // disabled={!(partner.totalReturn && partner.totalReturn > 0) || checkedInvoice.length === 0}
                    disabled={checkedInvoice.length === 0}
                    checked={useReturn}
                    onChange={handleUseReturn}
                    color='primary'
                  />
                }
                label={`Gunakan Invoice Return ${(partner.totalReturn && partner.totalReturn > 0 && '(Supplier Ini memiliki Jumlah Return)') || ''}`}
              />
            </Grid>
            <Fade in={useReturn}>
              <Grid container direction='row' item xl={12} md={12} sm={12}>
                <Alert severity='warning' style={{ marginBottom: '16px' }}>
                  <strong>Perhatian:</strong> Invoice return yang telah digunakan sebelumnya akan otomatis <strong>dihapus</strong> dari sistem jika
                  status centangnya diubah (uncheck). Pastikan Anda yakin sebelum mengubah status.
                </Alert>
                <InvoiceReturnTable
                  isLoadingData={isLoadingData || isUpdateLoading}
                  checked={checkedInvoiceReturn}
                  tempInvoice={tempReturn}
                  invoicePayment={checkedReturn}
                  handleIndividualCheck={handleReturnInvoiceCheck}
                />
              </Grid>
            </Fade>
            <Fade in={useReturn}>
              <Grid container direction='row' item xl={12} md={12} sm={12} spacing={1}>
                <Grid item>
                  <Button disabled={!useReturn} variant='outlined' onClick={handleOpenReturn}>
                    Tambahkan Return
                  </Button>
                </Grid>
              </Grid>
            </Fade>
            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Grid item>
                <Typography variant='subtitle2'>Jumlah Pembayaran (Total Sisa Pembayaran) :</Typography>
              </Grid>

              <Grid item>
                <NumberFormat value={totalPrice} prefix={'Rp'} thousandSeparator={true} displayType='text' />
              </Grid>
            </Grid>

            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Grid item>
                <Typography variant='subtitle2'>Jumlah Return Telah Digunakan :</Typography>
              </Grid>

              <Grid item>
                <NumberFormat value={currentTotalReturn} prefix={'-Rp'} thousandSeparator={true} displayType='text' />
              </Grid>
            </Grid>

            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Grid item>
                <Typography variant='subtitle2'>Jumlah Return Yang Akan Digunakan :</Typography>
              </Grid>

              <Grid item>
                <NumberFormat value={totalReturn} prefix={'-Rp'} thousandSeparator={true} displayType='text' />
              </Grid>
            </Grid>

            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Grid item>
                <Typography variant='subtitle2'>Jumlah Potongan Saat Ini:</Typography>
              </Grid>

              <Grid item>
                <NumberFormat value={currentOtherPiece} prefix={'Rp'} thousandSeparator={true} displayType='text' />
              </Grid>
            </Grid>

            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <TextField
                type='number'
                label='Potongan Lainya'
                fullWidth={isSmall}
                value={otherPiece !== null ? formatNumberManual(otherPiece) : ''}
                // error={totalPayError !== ''}
                onChange={e => handleChange(e.target.value.replace(/\./g, ''))}
                InputProps={{
                  startAdornment: <span>Rp</span>
                }}
              />
            </Grid>

            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Fade in={!isFullReturn}>
                <RadioGroup row aria-label='position' name='position' defaultValue='end'>
                  <FormControlLabel
                    onChange={handleFullPayment}
                    value='1'
                    control={<Radio color='primary' checked={fullPayment} />}
                    label='Bayar Full'
                    labelPlacement='end'
                  />
                  <FormControlLabel
                    onChange={handleNotFullPayment}
                    value='0'
                    control={<Radio color='primary' checked={!fullPayment} />}
                    label='Bayar Sebagian'
                    labelPlacement='end'
                  />
                </RadioGroup>
              </Fade>
            </Grid>
            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Grid item>
                <Typography variant='subtitle2'>Total Terbayar Saat Ini:</Typography>
              </Grid>

              <Grid item>
                <NumberFormat value={currentTotalPay + currentTotalReturn} prefix={'Rp'} thousandSeparator={true} displayType='text' />
              </Grid>
            </Grid>
            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Fade in={!isFullReturn}>
                <TextField
                  label='Jumlah Bayar'
                  fullWidth={isSmall}
                  disabled={fullPayment}
                  value={totalPay}
                  helperText={totalPayError}
                  error={totalPayError !== ''}
                  onChange={e => {
                    handleChangeTotalPay(+e.target.value);
                  }}
                  InputProps={{
                    inputComponent: NumberFormatMask as any
                  }}
                />
              </Fade>
            </Grid>
            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Grid item>
                <Typography variant='subtitle2'>Total Bayar (Jumlah Bayar + Total Return) :</Typography>
              </Grid>

              <Grid item>
                <NumberFormat value={totalPay + totalReturn} prefix={'Rp'} thousandSeparator={true} displayType='text' />
              </Grid>
            </Grid>
            <Grid container direction='row' justify={isSmall ? 'space-between' : 'flex-end'} spacing={2} item xl={12} md={12} sm={12}>
              <Grid item xl={2} lg={2} md={12} sm={12} xs={12}>
                <Button variant={isSmall ? 'outlined' : 'contained'} onClick={handleCancel} fullWidth style={{ color: '#bdbdbd' }} color='inherit'>
                  Batal
                </Button>
              </Grid>
              <Grid item xl={2} lg={2} md={12} sm={12} xs={12}>
                <Button disabled={isSubmit || isRefresh} fullWidth onClick={handleOnSubmit}>
                  {isSubmit ? <CircularProgress color='inherit' size={20} /> : 'Simpan'}
                </Button>
              </Grid>
            </Grid>
            <StandardConfirmationDialog
              variant={snackbarVariant}
              titleMessage={snackbarVariant === 'success' ? 'Success!' : 'Error!'}
              message={message}
              open={openSnackbar}
              handleClose={snackbarVariant === 'success' ? handleConfirmSnackbar : handleCloseSnackbar}
              onConfirm={handleConfirmSnackbar}
              noCancelButton={true}
            />
            <StandardConfirmationDialogGiro
              variant={snackbarVariant}
              titleMessage={snackbarVariant === 'success' ? 'Success!' : 'Error!'}
              message={message}
              open={openSnackbarGiro}
              handleClose={handleCloseSnackbarGiro}
              onConfirm={handleConfirmSnackbarGiro}
              noCancelButton={true}
            />
            <GiroModal
              handleAddGiro={handleAddGiro}
              giro={giro}
              open={isCreateGiro}
              handleCloseCreate={handleClickCLoseGiroModal}
              initialGiro={editingGiro}
            />
            <InvoiceModal
              checkedInvoice={checkedInvoice}
              openInvoice={openInvoice}
              queryString={queryString}
              paymentMessageError={paymentMessageError}
              order={order}
              orderBy={orderBy}
              isLoadingData={isLoadingData}
              invoices={invoices}
              count={count}
              currentPage={currentPage}
              handleClose={handleCloseInvoiceModal}
              setQueryString={setQueryString}
              handleRequestSort={handleRequestSort}
              setCurrentPage={setCurrentPage}
              handleIndividualCheck={individualCheck}
              handleCancelInvoice={handleCancelInvoice}
            />

            <InvoiceReturnModal
              checked={checkedInvoiceReturn}
              openInvoice={openReturn}
              paymentMessageError={''}
              isLoadingData={isLoadingReturn}
              invoices={invoiceReturn}
              queryString={returnNumber}
              setQueryString={setReturnNumber}
              handleClose={handleCloseReturn}
              handleIndividualCheck={handleReturnInvoiceCheck}
            />

            <ConfirmPaymentModal
              open={openConfirm}
              editedPaymentId={+(id || 0)}
              isLoading={isSubmit}
              handleClose={handleCloseConfirm}
              transferDate={transferDate}
              handleConfirm={handleDoSubmit}
              invoice={checkedInvoice}
              invoiceReturn={checkedInvoiceReturn}
              payDate={payDate}
              otherPiece={otherPiece}
              bankId={bank.id}
              bankName={bank.bankName}
              partnerName={partner.name}
              paymentMethod={paymentMethod}
              note={note}
              totalPay={totalPay}
              totalPrice={totalPrice}
              totalReturn={totalReturn}
              useReturn={checkedReturn.length > 0}
              giroBank={giroBank}
              giroDueDate={giroDueDate}
              giroAmount={giroAmount}
              giroNumber={giroNumber}
            />
          </Grid>
        </PaperCustom>
      </Container>
    </Page>
  );
};

export default PurchasePaymentUpdatePage;
